import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { authEnabled, auth0Config } from './config';
import { setTokenGetter } from '../api/authInterceptor';

/**
 * Wires up the token source for the module-level axios request interceptor
 * in `src/api/authInterceptor.ts`. The interceptor itself is registered
 * once at module load; this component just sets/clears the token getter
 * as the auth state changes.
 *
 * The audience is passed explicitly from shared config so the token is a
 * JWT for our backend API, not an opaque token for the Auth0 Management
 * API (the old default was the latter — every backend call would 401).
 */
const TokenAttacherInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (isAuthenticated) {
      setTokenGetter(() =>
        getAccessTokenSilently({
          authorizationParams: { audience: auth0Config.audience },
        }),
      );
    } else {
      setTokenGetter(null);
    }
    return () => setTokenGetter(null);
  }, [isAuthenticated, getAccessTokenSilently]);

  return <>{children}</>;
};

const AuthTokenAttacher: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!authEnabled) return <>{children}</>;
  return <TokenAttacherInner>{children}</TokenAttacherInner>;
};

export default AuthTokenAttacher;
